import { timing } from 'constants/ui.constants';
import { format, isValid } from 'date-fns';
import _ from 'lodash';
import { IUserPermission, userTitles } from 'constants/user.constants';

interface ValidationField {
  key: string;
  displayName?: string;
}

const convertToCamelCase = (str: string) => str.replace(/_[a-z]/g, (match: string) => `${match[1].toUpperCase()}`);

export const convertKeysToCamelCase = <T = any>(obj: Record<string, any>): T => {
  const res: Record<string, any> = {};
  _.each(_.keys(obj), (key: string) => {
    res[convertToCamelCase(key)] = obj[key];
  });
  return res as T;
};

export const convertToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter: string) => `_${letter.toLowerCase()}`).replace(/^_/, '');

export const convertKeysToSnakeCase = (obj: Record<string, any>): any => {
  const res: Record<string, any> = {};
  _.each(_.keys(obj), (key: string) => {
    //remove spaces from the beginning and end of the string
    obj[key] = typeof obj[key] === 'string' ? obj[key]?.toString().trim() : obj[key];
    res[convertToSnakeCase(key)] = obj[key];
  });
  return res;
};

export const formatFieldName = (fieldName: string): string => {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

export const convertKeysToCamelCaseArray = <T = any>(objs: Record<string, any>[]): T[] => {
  const res: Record<string, any>[] = [];
  objs.forEach((obj) => {
    res.push(convertKeysToCamelCase(obj));
  });
  return res as T[];
};

export const getFormattedDate = (d: Date, timeFormat?: timing, defaultValue?: string) =>
  !_.isNull(d) && isValid(new Date(d)) ? format(new Date(d), timeFormat || timing.DATE_MONTH) : defaultValue || d;

type UnknownArrayOrObject = unknown[] | Record<string, unknown>;

export const dirtyValues = (
  dirtyFields: UnknownArrayOrObject | boolean,
  allValues: UnknownArrayOrObject
): UnknownArrayOrObject => {
  // NOTE: Recursive function.

  // If *any* item in an array was modified, the entire array must be submitted, because there's no
  // way to indicate "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return allValues;
  }

  // Here, we have an object.
  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [key, dirtyValues((dirtyFields as any)[key], (allValues as any)[key])])
  );
};

export function generateSequentialArray(i: number, j: number): number[] {
  return Array.from(Array(j - i + 1).keys(), (num) => num + i);
}

export const customSort = (aName: any, bName: any, term: string) => {
  const aContainsTerm = aName?.toLowerCase()?.includes(term);
  const bContainsTerm = bName?.toLowerCase()?.includes(term);

  if (aContainsTerm && !bContainsTerm) return -1;
  if (!aContainsTerm && bContainsTerm) return 1;

  return 0;
};

export const isPhoneNumberValid = (value: string) => {
  const phoneNumberRegex = /^\+\d{1,3}\d{10}$/;
  return !(value?.length > 0 && !phoneNumberRegex.test(value));
};

export const getUniqRolesSet = (user: IUserPermission | null) => {
  if (user) {
    return new Set(user.sitePermissions?.map((permission) => userTitles[permission.role as keyof typeof userTitles]));
  }
  return [];
};

export const getUserRoleType = (user: IUserPermission | null) => {
  return userTitles[user?.role as keyof typeof userTitles] || 'N/A';
};

export const filterByFields = (data: any[], value: string, fields: string[], combinedFields: string[][] = []) => {
  const normalizeSpaces = (str: string) => str.replace(/\s+/g, ' ').trim();
  const lowerValue = normalizeSpaces(value.toLowerCase());

  if (!lowerValue.length) {
    return data;
  }

  return data.filter((item) => {
    const matchesIndividualFields = fields.some((field) => {
      const safeFieldValue = normalizeSpaces((item[field] || '').toLowerCase());
      return safeFieldValue.includes(lowerValue);
    });

    const matchesCombinedFields = combinedFields.some((fieldGroup) => {
      const values = fieldGroup.map((field) => item[field] || '');
      const forwardCombined = normalizeSpaces(values.join(' ').toLowerCase());
      const reverseCombined = normalizeSpaces([...values].reverse().join(' ').toLowerCase());
      return forwardCombined.includes(lowerValue) || reverseCombined.includes(lowerValue);
    });

    return matchesIndividualFields || matchesCombinedFields;
  });
};

export const formResolver = <T extends Record<string, any>>(values: T, fields: (string | ValidationField)[]) => {
  const errors: Record<string, any> = { dataForm: {} };
  const dataForm = values.dataForm || {};

  fields.forEach((field) => {
    const key = typeof field === 'string' ? field : field.key;
    const displayName = typeof field === 'string' ? formatFieldName(key) : field.displayName || formatFieldName(key);

    if (!dataForm[key] || (typeof dataForm[key] === 'string' && dataForm[key].trim() === '')) {
      errors.dataForm[key] = {
        type: 'required',
        message: `${displayName} is required`
      };
    }
  });

  return {
    values,
    errors: Object.keys(errors.dataForm).length ? errors : {}
  };
};
